import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useAltHotkeys } from '../hooks/useAltHotkeys';
import { useModalDrag } from '../hooks/useModalDrag';
import ModalHeader from './ui/ModalHeader';

/**
 * A small "enter a required reason" prompt — a textarea + Cancel/Confirm, portaled to
 * <body>. Used by the Pending EDI Orders board for Reject (990 reason) and Delete
 * (delete reason), single or batch. Confirm stays disabled until a non-empty reason is
 * entered; an optional `note` warns about skipped rows (e.g. already-accepted orders).
 */
export interface ReasonPromptModalProps {
  title: string;
  /** Label above the textarea (e.g. "Rejection reason", "Delete reason"). */
  label: string;
  confirmLabel: string;
  /** Optional warning line shown above the textarea (e.g. "3 already accepted will be skipped"). */
  note?: string;
  /** Danger styling for the confirm button (delete). */
  danger?: boolean;
  saving?: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

export function ReasonPromptModal({
  title, label, confirmLabel, note, danger, saving, onCancel, onConfirm,
}: ReasonPromptModalProps) {
  const [reason, setReason] = useState('');
  const valid = reason.trim() !== '';

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel, saving]);

  // Rendered only while open. Alt+C cancels, alongside the Escape handler above.
  // Confirm gets no chord: the caller supplies the label, and it's Reject or
  // Delete in practice. The layer still swallows Alt+S so it can't reach the grid.
  useAltHotkeys({ C: saving ? null : onCancel }, undefined, { layer: true });

  // Unmounted by the parent when closed, so `true` is fine — unmount resets state.
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(true);

  const confirmCls = danger
    ? 'bg-red-600 hover:bg-red-700'
    : 'bg-accent hover:bg-accent-hover';

  return createPortal(
    <div
      className="fixed inset-0 z-[130] bg-slate-900/40 flex items-center justify-center p-6"
      role="dialog"
      aria-label={title}
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onCancel(); }}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-slate-200 dark:border-gray-700 w-full max-w-md"
      >
        <ModalHeader
          title={title}
          onClose={saving ? undefined : onCancel}
          dragHandleProps={dragHandleProps}
        />
        <div className="px-4 py-4 space-y-3">
          {note && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/30 px-3 py-2 text-[0.78125rem] text-amber-800 dark:text-amber-200">
              <ExclamationTriangleIcon className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{note}</span>
            </div>
          )}
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
            {label} <span className="text-red-500">*</span>
          </label>
          <textarea
            autoFocus
            rows={4}
            value={reason}
            disabled={saving}
            onChange={(e) => setReason(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
          />
        </div>
        <div className="flex items-center justify-end gap-3 px-4 py-3 border-t border-slate-200 dark:border-gray-700">
          <button type="button" onClick={onCancel} disabled={saving}
            className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
            Cancel
          </button>
          <button type="button" onClick={() => valid && onConfirm(reason.trim())} disabled={!valid || saving}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 inline-flex items-center gap-2 ${confirmCls}`}>
            {saving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving…</>) : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default ReasonPromptModal;
